import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Telegram as TelegramIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Circle as CircleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// Mock data
const conversations = [
  {
    id: '1',
    contactName: 'João Santos',
    channel: 'whatsapp',
    lastMessage: 'Gostaria de saber mais sobre os planos disponíveis',
    time: '14:30',
    unread: 3,
    status: 'online',
    avatar: null,
  },
  {
    id: '2',
    contactName: 'Maria Silva',
    channel: 'instagram',
    lastMessage: 'Obrigada pelo atendimento!',
    time: '13:45',
    unread: 0,
    status: 'offline',
    avatar: null,
  },
  {
    id: '3',
    contactName: 'Pedro Costa',
    channel: 'telegram',
    lastMessage: 'Preciso de ajuda com o produto',
    time: '12:20',
    unread: 1,
    status: 'online',
    avatar: null,
  },
];

const messages = [
  {
    id: '1',
    content: 'Olá! Como posso ajudá-lo hoje?',
    time: '14:30',
    direction: 'outbound',
  },
  {
    id: '2',
    content: 'Gostaria de saber mais sobre os planos disponíveis',
    time: '14:32',
    direction: 'inbound',
  },
];

const Inbox: React.FC = () => {
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [messageText, setMessageText] = useState('');
  const [showChatList, setShowChatList] = useState(true);

  // Mock para detectar mobile
  const isMobile = window.innerWidth < 768;

  const currentConversation = conversations.find(c => c.id === selectedConversation);

  const filteredConversations = conversations.filter(conversation => {
    const matchesSearch = conversation.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conversation.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesChannel = channelFilter === 'all' || conversation.channel === channelFilter;
    return matchesSearch && matchesChannel;
  });

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversation(conversationId);
    if (isMobile) {
      setShowChatList(false);
    }
  };

  const handleSendMessage = () => {
    if (messageText.trim()) {
      console.log('Enviando mensagem:', messageText);
      setMessageText('');
    }
  };

  const handleBackToList = () => {
    setShowChatList(true);
    if (isMobile) {
      setSelectedConversation('');
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return '#25D366';
      case 'whatsapp_onboarding': return '#25D366';
      case 'whatsapp_recovery': return '#25D366';
      case 'instagram': return '#E4405F';
      case 'telegram': return '#0088CC';
      default: return '#666';
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return <WhatsAppIcon />;
      case 'whatsapp_onboarding': return <WhatsAppIcon />;
      case 'whatsapp_recovery': return <WhatsAppIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'telegram': return <TelegramIcon />;
      default: return <ChatIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return '#4CAF50';
      case 'away': return '#FF9800';
      case 'offline': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header com título e filtros */}
      <Box sx={{ 
        mb: { xs: 2, md: 3 }, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
        padding: { xs: '16px', md: '20px 24px' },
        position: 'relative',
        zIndex: 1,
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#475569',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 0.5,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Inbox Omnichannel
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Gerencie todas as conversas de diferentes canais em um só lugar
          </Typography>
        </Box>
      </Box>

      {/* Mobile: Chat em tela cheia quando selecionado */}
      {isMobile && selectedConversation && !showChatList ? (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header do chat mobile */}
          <Box className="sophisticated-card" sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--border-radius-medium)',
            boxShadow: 'var(--shadow-medium)',
            minHeight: '60px',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
            },
          }}>
            <IconButton 
              onClick={handleBackToList}
              sx={{ mr: 2, color: 'primary.main' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar sx={{ mr: 2, bgcolor: getChannelColor(currentConversation?.channel || '') }}>
              {getChannelIcon(currentConversation?.channel || '')}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currentConversation?.contactName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircleIcon 
                  sx={{ 
                    fontSize: 8, 
                    color: getStatusColor(currentConversation?.status || '') 
                  }} 
                />
                <Typography variant="caption" color="text.secondary">
                  {currentConversation?.status === 'online' ? 'Online' : 'Offline'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Área de mensagens mobile */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              overflow: 'auto',
              bgcolor: '#f0f2f5'
            }}>
              {/* Mensagens do chat */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Mensagem recebida */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start',
                  maxWidth: '80%'
                }}>
                  <Box className="sophisticated-card" sx={{
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    p: 2,
                    borderRadius: '18px 18px 18px 4px',
                    boxShadow: 'var(--shadow-medium)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 'var(--shadow-large)',
                    },
                  }}>
                    <Typography variant="body2">
                      {currentConversation?.lastMessage}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {currentConversation?.time}
                    </Typography>
                  </Box>
                </Box>

                {/* Mensagem enviada */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  maxWidth: '80%',
                  alignSelf: 'flex-end'
                }}>
                  <Box className="sophisticated-card" sx={{
                    background: 'var(--gradient-primary)',
                    color: '#000000',
                    p: 2,
                    borderRadius: '18px 18px 4px 18px',
                    boxShadow: 'var(--shadow-medium)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 'var(--shadow-large)',
                    },
                  }}>
                    <Typography variant="body2">
                      Olá! Como posso ajudá-lo hoje?
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                      14:32
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Input de mensagem mobile */}
            <Box className="sophisticated-card" sx={{
              p: 2,
              background: 'var(--glass-bg)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--border-radius-medium)',
              boxShadow: 'var(--shadow-medium)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
              },
            }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton 
                  size="small"
                  className="sophisticated-button"
                  sx={{
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--border-radius-small)',
                    boxShadow: 'var(--shadow-small)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 'var(--shadow-medium)',
                    },
                  }}
                >
                  <AttachFileIcon />
                </IconButton>
                <TextField
                  fullWidth
                  placeholder="Digite sua mensagem..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  size="small"
                  className="sophisticated-input"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'var(--glass-bg)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '20px',
                      boxShadow: 'var(--shadow-small)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 'var(--shadow-medium)',
                      },
                      '&.Mui-focused': {
                        boxShadow: 'var(--shadow-large)',
                        borderColor: 'var(--primary-color)',
                      },
                    },
                  }}
                />
                <IconButton 
                  size="small"
                  className="sophisticated-button"
                  sx={{
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--border-radius-small)',
                    boxShadow: 'var(--shadow-small)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 'var(--shadow-medium)',
                    },
                  }}
                >
                  <EmojiEmotionsIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={handleSendMessage}
                  className="sophisticated-button"
                  sx={{
                    background: 'var(--gradient-primary)',
                    color: '#000000',
                    borderRadius: 'var(--border-radius-small)',
                    boxShadow: 'var(--shadow-small)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 'var(--shadow-large)',
                    },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Desktop: Layout normal */
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0, md: 3 }, 
          height: { xs: '100%', md: 'calc(100vh - 200px)' },
          flex: 1
        }}>
          {/* Lista de Conversas */}
          <Box sx={{ 
            flex: { xs: showChatList ? '1' : '0', md: '0 0 400px' },
            minWidth: { xs: 'auto', md: 400 },
            display: { xs: showChatList ? 'block' : 'none', md: 'block' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 2 : 1, md: 1 },
            position: { xs: 'relative', md: 'static' }
          }}>
            <Card className="sophisticated-card" sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: { xs: 0, md: '12px' },
              background: 'rgba(31, 41, 55, 0.4)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(59, 130, 246, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.3)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(59, 130, 246, 0.2)',
              },
            }}>
              <CardContent sx={{ pb: 1 }}>
                {/* Busca */}
                <TextField
                  fullWidth
                  placeholder="Buscar conversas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="sophisticated-input"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(31, 41, 55, 0.3)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                        background: 'rgba(31, 41, 55, 0.4)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Filtros por Canal */}
                <Tabs
                  value={channelFilter}
                  onChange={(_, value) => setChannelFilter(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ 
                    mb: 2, 
                    minHeight: 40,
                    '& .MuiTab-root': {
                      background: 'rgba(31, 41, 55, 0.3)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      margin: '0 4px',
                      transition: 'all 0.3s ease',
                      color: 'text.secondary',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        background: 'rgba(59, 130, 246, 0.1)',
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                      },
                      '&.Mui-selected': {
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: 'rgba(59, 130, 246, 0.9)',
                        borderColor: 'rgba(59, 130, 246, 0.4)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                      },
                    },
                  }}
                >
                  <Tab label="Todos" value="all" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
                  <Tab 
                    icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} 
                    label="WhatsApp" 
                    value="whatsapp"
                    sx={{ minHeight: 40, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} 
                    label="WhatsApp Onboarding" 
                    value="whatsapp_onboarding"
                    sx={{ minHeight: 40, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} 
                    label="WhatsApp Recuperação" 
                    value="whatsapp_recovery"
                    sx={{ minHeight: 40, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    icon={<InstagramIcon sx={{ fontSize: 18 }} />} 
                    label="Instagram" 
                    value="instagram"
                    sx={{ minHeight: 40, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    icon={<TelegramIcon sx={{ fontSize: 18 }} />} 
                    label="Telegram" 
                    value="telegram"
                    sx={{ minHeight: 40, fontSize: '0.875rem' }}
                  />
                </Tabs>
              </CardContent>

              <Divider />

              {/* Lista de Conversas */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <List sx={{ p: 0 }}>
                  {filteredConversations.map((conversation) => (
                    <ListItem key={conversation.id} disablePadding>
                      <ListItemButton
                        selected={selectedConversation === conversation.id}
                        onClick={() => handleSelectConversation(conversation.id)}
                        className="sophisticated-menu-item"
                        sx={{
                          borderLeft: selectedConversation === conversation.id ? 3 : 0,
                          borderColor: selectedConversation === conversation.id ? 'rgba(59, 130, 246, 0.8)' : 'transparent',
                          background: selectedConversation === conversation.id 
                            ? 'rgba(59, 130, 246, 0.15)' 
                            : 'rgba(31, 41, 55, 0.2)',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                          border: selectedConversation === conversation.id 
                            ? '1px solid rgba(59, 130, 246, 0.3)' 
                            : '1px solid rgba(59, 130, 246, 0.1)',
                          borderRadius: '8px',
                          boxShadow: selectedConversation === conversation.id 
                            ? '0 4px 12px rgba(59, 130, 246, 0.2)' 
                            : '0 2px 8px rgba(0, 0, 0, 0.1)',
                          margin: '4px 8px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            background: 'rgba(59, 130, 246, 0.1)',
                            borderColor: 'rgba(59, 130, 246, 0.3)',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                          },
                          '&.Mui-selected': {
                            background: 'rgba(59, 130, 246, 0.15)',
                            color: '#000000',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Badge
                            badgeContent={conversation.unread || null}
                            color="error"
                          >
                            <Avatar
                              sx={{
                                bgcolor: conversation.avatar ? 'transparent' : getChannelColor(conversation.channel),
                                border: 2,
                                borderColor: getChannelColor(conversation.channel),
                              }}
                            >
                              {conversation.avatar || getChannelIcon(conversation.channel)}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {conversation.contactName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircleIcon 
                                  sx={{ 
                                    fontSize: 8, 
                                    color: getStatusColor(conversation.status),
                                  }} 
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {conversation.time}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '200px',
                                }}
                              >
                                {conversation.lastMessage}
                              </Typography>
                              <Chip
                                label={conversation.channel.toUpperCase()}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: getChannelColor(conversation.channel),
                                  color: '#000000',
                                  ml: 1,
                                }}
                              />
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Card>
          </Box>

          {/* Área de Chat */}
          <Box sx={{ 
            flex: { xs: showChatList ? '0' : '1', md: 1 },
            minWidth: 0,
            display: { xs: showChatList ? 'none' : 'block', md: 'block' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 1 : 2, md: 1 },
            position: { xs: 'relative', md: 'static' }
          }}>
            <Card className="sophisticated-card" sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: { xs: 0, md: '12px' },
              background: 'rgba(31, 41, 55, 0.4)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(59, 130, 246, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.3)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(59, 130, 246, 0.2)',
              },
            }}>
              {currentConversation ? (
                <>
                  {/* Header do Chat */}
                  <CardContent className="sophisticated-card" sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider', 
                    py: 2,
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--border-radius-medium)',
                    boxShadow: 'var(--shadow-medium)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Botão voltar - só aparece no mobile */}
                      <IconButton
                        onClick={handleBackToList}
                        sx={{
                          display: { xs: 'flex', md: 'none' },
                          mr: 1,
                          color: 'primary.main'
                        }}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                      
                      <Avatar sx={{ mr: 2, bgcolor: getChannelColor(currentConversation.channel) }}>
                        {getChannelIcon(currentConversation.channel)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {currentConversation.contactName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircleIcon 
                            sx={{ 
                              fontSize: 8, 
                              color: getStatusColor(currentConversation.status),
                            }} 
                          />
                          <Typography variant="body2" color="text.secondary">
                            {currentConversation.status === 'online' ? 'Online' : 
                             currentConversation.status === 'away' ? 'Ausente' : 'Offline'}
                          </Typography>
                          <Chip
                            label={currentConversation.channel.toUpperCase()}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: getChannelColor(currentConversation.channel),
                              color: '#000000',
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>

                  {/* Mensagens */}
                  <Box className="sophisticated-card" sx={{ 
                    flex: 1, 
                    overflow: 'auto', 
                    p: 2,
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--border-radius-medium)',
                    boxShadow: 'var(--shadow-medium)',
                    minHeight: '500px',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                      opacity: 0.5
                    }
                  }}>
                    {messages.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                          mb: 2,
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            bgcolor: message.direction === 'outbound' 
                              ? '#DCF8C6'
                              : '#FFFFFF',
                            color: '#475569',
                            p: 2,
                            borderRadius: 3,
                            boxShadow: message.direction === 'outbound' 
                              ? '0 2px 4px rgba(37, 211, 102, 0.2)'
                              : '0 2px 4px rgba(0,0,0,0.15)',
                            border: 2,
                            borderColor: message.direction === 'outbound' 
                              ? '#B8E6B8'
                              : '#E0E0E0',
                          }}
                        >
                          <Typography variant="body2" sx={{ 
                            mb: 0.5,
                            lineHeight: 1.4,
                            color: '#475569',
                            fontWeight: 500
                          }}>
                            {message.content}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#65676B',
                              fontSize: '0.75rem',
                              display: 'block'
                            }}
                          >
                            {message.time}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Input de Mensagem */}
                  <Box sx={{ 
                    p: 2, 
                    borderTop: 1, 
                    borderColor: 'divider',
                    bgcolor: '#000000',
                    boxShadow: '0 -1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <IconButton size="small" sx={{ 
                        color: '#65676B',
                        '&:hover': { bgcolor: '#F2F3F5' }
                      }}>
                        <AttachFileIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ 
                        color: '#65676B',
                        '&:hover': { bgcolor: '#F2F3F5' }
                      }}>
                        <EmojiEmotionsIcon />
                      </IconButton>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        placeholder="Digite sua mensagem..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        variant="outlined"
                        size="small"
                        className="sophisticated-input"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: 'var(--glass-bg)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: 'var(--border-radius-medium)',
                            boxShadow: 'var(--shadow-small)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: 'var(--shadow-medium)',
                            },
                            '&.Mui-focused': {
                              boxShadow: 'var(--shadow-large)',
                              borderColor: 'var(--primary-color)',
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: 'var(--text-primary)',
                            '&::placeholder': {
                              color: 'var(--text-secondary)',
                              opacity: 1
                            }
                          }
                        }}
                      />
                      <IconButton
                        className="sophisticated-button"
                        sx={{ 
                          background: 'var(--gradient-primary)',
                          color: '#000000',
                          borderRadius: 'var(--border-radius-small)',
                          boxShadow: 'var(--shadow-small)',
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-2px)',
                            boxShadow: 'var(--shadow-large)',
                          },
                          '&:disabled': { 
                            background: 'var(--glass-bg)',
                            color: 'var(--text-disabled)',
                            boxShadow: 'none',
                            transform: 'none',
                          }
                        }}
                        onClick={handleSendMessage}
                        disabled={!messageText.trim()}
                      >
                        <SendIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box
                  className="sophisticated-card"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2,
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--border-radius-medium)',
                    boxShadow: 'var(--shadow-medium)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                    },
                  }}
                >
                  <ChatIcon sx={{ 
                    fontSize: 64, 
                    color: 'var(--text-secondary)',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  }} />
                  <Typography variant="h6" sx={{
                    color: 'var(--text-secondary)',
                    textAlign: 'center',
                    fontWeight: 500,
                  }}>
                    Selecione uma conversa para começar
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Inbox;